import { useState } from 'preact/hooks';
import { ANSWER_MAX, type ClientView } from '@gazette/shared';
import { locale, t } from '../../i18n/index.ts';
import { actions, me } from '../../net/client.ts';
import { ErrorNotice, GameHeader } from '../components/Chrome.tsx';
import { Portrait } from '../components/Portrait.tsx';
import { Progress } from '../components/Progress.tsx';
import { useAutosave, type PhaseOf } from '../hooks.ts';

export function Confessions({ current, phase }: { current: ClientView; phase: PhaseOf<'confessions'> }) {
  const player = me.value!;
  const [answers, setAnswers] = useState<string[]>(() => phase.answers);
  const [signed, setSigned] = useState(phase.done);
  const hasAnswer = answers.some((answer) => answer.trim().length > 0);
  const number = current.players.findIndex((candidate) => candidate.id === player.id) + 1;

  const cancelAutosave = useAutosave(answers, (value) => void actions.confessions(value, signed));

  const sign = (done: boolean) => {
    cancelAutosave();
    setSigned(done);
    void actions.confessions(answers, done);
  };

  return (
    <>
      <GameHeader />
      <section class="sheet sheet--full" data-occlude aria-labelledby="statement-title">
        <form
          class="fiche-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (hasAnswer) sign(true);
          }}
        >
          <header class="fiche-form__header">
            <div class="stamped-portrait">
              <Portrait seed={player.portrait} width={72} />
              <span class="violet-stamp stamped-portrait__stamp">{t('fiche.number', { n: String(number).padStart(2, '0') })}</span>
            </div>
            <div>
              <h2 id="statement-title" class="fiche-form__title">
                {t('conf.title')}
              </h2>
              <p class="fiche-form__name">{player.name}</p>
            </div>
          </header>
          <p class="fiche-form__intro">{t('conf.intro')}</p>

          {phase.questions.map((question, index) => (
            <label class="field field--question">
              <span class="field__question">{question.text[locale.value]}</span>
              <textarea
                class="typed typed--ruled"
                rows={2}
                maxLength={ANSWER_MAX}
                value={answers[index]}
                placeholder={t('conf.placeholder')}
                readOnly={signed}
                onInput={(event) => {
                  const answer = event.currentTarget.value;
                  setAnswers((previous) => previous.map((current, position) => (position === index ? answer : current)));
                }}
              />
            </label>
          ))}

          <div class="fiche-form__actions">
            {signed ? (
              <>
                <p class="violet-stamp violet-stamp--large" role="status">
                  {t('conf.signed')}
                </p>
                <button type="button" class="btn btn--link" onClick={() => sign(false)}>
                  {t('conf.edit')}
                </button>
              </>
            ) : (
              <>
                {!hasAnswer && <p class="hint hint--ink">{t('conf.needOne')}</p>}
                <button type="submit" class="btn btn--ink btn--wide" disabled={!hasAnswer}>
                  {t('conf.sign')}
                </button>
              </>
            )}
          </div>
        </form>
        <ErrorNotice />
        <Progress progress={phase.progress} label={t('progress.signed', phase.progress)} />
      </section>
    </>
  );
}
